// System
import React, { useMemo } from 'react';

// Components
import { TableBodyEx, TableContainerEx, TableEx } from '@Components/shared/tables/components';
import {
  ColumnVisibilityPresenter,
  TableHeadColumnsPresenter,
  TableHeadPresenter,
  TableRowPresenter,
} from '@Components/shared/tables/presenters';
import { LoadingScreen } from '@Components/shared/loaders/LoadingScreen';

// Utils
import { applyColumnVisibility } from '@utils/data/applyColumnVisibility';

//  Table
import { useConfigsTableColumns } from './useConfigsTableColumns';

// Redux
import { useConfigsTable } from '@Modal/hooks/providers/useConfigsTable';
import { useProviderDrawer } from '@Modal/hooks/providers/useProviderDrawer';

// Hooks
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';

// GraphQL
import { useGetProviderConfigsQuery } from '@Src/generated.graphql';
import { ConfigItemDto } from '@contracts/api/dto/providers/ConfigDto';
import { useUpdateProviderConfig } from '@Modal/hooks/providers/useUpdateProviderConfig';
import { useGeneral } from '@Modal/hooks/general/useGeneral';
import { useUpdateMinesProviderConfig } from '@Modal/hooks/providers/useUpdateMinesProviderConfig';

export const ConfigsTable: React.FC = () => {
  const { game } = useGeneral();
  const { configsListFilters, columnVisibility, visiblityCustomizable, configTableFilters } =
    useConfigsTable();
  const { handleConfigRowClick } = useUpdateProviderConfig();
  const { handleConfigRowClick: handleMinesConfigRowClick } = useUpdateMinesProviderConfig();
  const { selectedProviderData } = useProviderDrawer();
  const columns = useConfigsTableColumns();
  const { userPermissions, currentUser } = useAuthorization();
  const canManageProviderConfigs =
    userPermissions?.includes('CAN_MANAGE_PROVIDER_CONFIGS') || currentUser.isSuper;
  const canViewProviderConfigs =
    userPermissions?.includes('CAN_VIEW_PROVIDER_CONFIGS') || currentUser.isSuper;
  const { data, loading } = useGetProviderConfigsQuery({
    variables: { id: selectedProviderData?.id, filter: configTableFilters },
  });
  const visibleColumns = useMemo(
    () => applyColumnVisibility(columns, configsListFilters),
    [columns, columnVisibility]
  );

  const handleRowClick = (row: ConfigItemDto) => {
    if (game === 1) {
      handleConfigRowClick(row);
    } else if (game === 2) {
      handleMinesConfigRowClick(row);
    }
  };

  const header = useMemo(
    () => (
      <TableHeadPresenter>
        <TableHeadColumnsPresenter columns={visibleColumns} />
        {/* @ts-ignore */}
        <ColumnVisibilityPresenter columns={columns} content={visiblityCustomizable} />
      </TableHeadPresenter>
    ),
    [columns]
  );

  if (!canViewProviderConfigs) return <h1>No Permission</h1>;
  if (loading) return <LoadingScreen />;
  if (!data?.providerConfigs?.length && !Object.entries(configTableFilters).length)
    return <strong>Current Casino has no configs</strong>;

  return (
    <>
      <TableContainerEx sx={{ height: '78vh' }} pageNumber={1}>
        <TableEx name="ConfigsTable">
          {header}
          <TableBodyEx>
            {/* @ts-ignore */}
            {data?.providerConfigs?.map((row: ConfigItemDto) => (
              <TableRowPresenter
                id={row.id}
                key={row.id}
                columns={visibleColumns}
                row={row}
                onClick={() => (canManageProviderConfigs ? handleRowClick(row) : null)}
              />
            ))}
          </TableBodyEx>
        </TableEx>
      </TableContainerEx>
    </>
  );
};
